<script setup lang="ts">
import type { SoMeIcon } from 'types';

const layout = useLayout();
const root = useRoot();

const footerIcons = ref<SoMeIcon[]>([
  {
    icon: 'facebook',
    link: root.settings?.content.facebook,
    title: root.clientName + ' Facebook'
  },
  {
    icon: 'twitter',
    link: root.settings?.content.twitter,
    title: root.clientName + ' Twitter'
  },
  {
    icon: 'youtube',
    link: root.settings?.content.youtube,
    title: root.clientName + ' YouTube'
  },
  {
    icon: 'instagram',
    link: root.settings?.content.instagram,
    title: root.clientName + ' Instagram'
  },
  {
    icon: 'linkedin',
    link: root.settings?.content.linkedin,
    title: root.clientName + ' LinkedIn'
  }
]);
</script>

<template>
  <footer class="relative mt-auto w-full bg-light-gray">
    <div class="container py-12 lg:py-16">
      <div
        v-if="root.settings?.content?.footerLogo"
        class="mb-8 flex flex-col gap-y-8 lg:mb-16 lg:flex-row lg:justify-between"
      >
        <TheLogo
          v-if="root.settings?.content?.footerLogo"
          :logo="root.settings?.content?.footerLogo"
          loading="lazy"
          class="block w-fit"
        />

        <FooterSoMe
          v-if="footerIcons?.length"
          :icons="footerIcons"
        />
      </div>

      <div
        v-if="layout.isDesktop"
        class="grid grid-cols-5 gap-6"
      >
        <div class="col-span-2 grid grid-cols-2 gap-6">
          <AddressInformation
            v-for="(address, addressIndex) in root.settings?.content.address"
            :key="`address-${addressIndex}`"
            :address="address"
            class="col-span-1 mb-8"
          />

          <div class="flex flex-col gap-2">
            <span class="flex gap-1">
              {{ $t('General.Phone') }}
              <BaseLink
                v-if="root.settings?.content.companyPhoneNumber"
                :to="`tel:${root.settings.content.companyPhoneNumber}`"
                class="underline"
              >
                {{ root.settings.content.companyPhoneNumber }}
              </BaseLink>
            </span>

            <span class="flex gap-1">
              {{ $t('General.Email') }}
              <BaseLink
                v-if="root.settings?.content.email"
                :to="`mailto:${root.settings.content.email}`"
                class="underline"
              >
                {{ root.settings.content.email }}
              </BaseLink>
            </span>
          </div>
        </div>

        <div class="col-span-2 col-start-4 text-black">
          <div
            v-if="root.settings?.content.quickLinks"
            class="grid grid-cols-2 gap-6"
          >
            <div
              v-for="quickLinkColumn in root.settings?.content.quickLinks"
              :key="quickLinkColumn.content.headline"
              class="col-span-1 flex justify-end"
            >
              <div>
                <p class="mb-2 font-bold">
                  {{ quickLinkColumn.content.headline }}
                </p>
                <nav>
                  <ul
                    :aria-label="$t('Aria.Global.Footer.QuickLinkList', { headline: quickLinkColumn.content.headline })"
                    class="grid gap-2"
                  >
                    <li
                      v-for="quickLink in quickLinkColumn.content.links"
                      :key="quickLink.name"
                      class="hyphens-auto"
                    >
                      <UmbracoLink
                        :link="quickLink"
                        :has-underline="true"
                        class="text-base"
                      />
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <BaseAccordionWrapper
          v-slot="slotProps"
          :expand-first-accordion-item="true"
        >
          <BaseAccordionItem
            v-if="root.settings?.content.address?.length"
            :key="0"
            :accordion-index="0"
            :is-open="slotProps.isActiveAccordion(0)"
            @update:is-open="slotProps.setActiveAccordion"
          >
            <template #title>
              <h3 v-if="root.settings.content.findDepartmentHeadline">
                {{ root.settings.content.findDepartmentHeadline }}
              </h3>
            </template>
            <AddressInformation
              v-for="(address, index) in root.settings?.content.address"
              :key="index"
              :address="address"
              class="mb-6"
            />

            <span
              v-if="root.settings?.content.companyPhoneNumber"
              class="flex gap-1 text-base"
            >
              {{ $t('General.Phone') }}
              <BaseLink
                :to="`tel:${root.settings.content.companyPhoneNumber}`"
                class="text-gray-70 underline"
              >
                {{ root.settings.content.companyPhoneNumber }}
              </BaseLink>
            </span>

            <span
              v-if="root.settings?.content.email"
              class="flex gap-1 text-base"
            >
              {{ $t('General.Email') }}
              <BaseLink
                :to="`mailto:${root.settings.content.email}`"
                class="text-gray-70 underline"
              >
                {{ root.settings.content.email }}
              </BaseLink>
            </span>
          </BaseAccordionItem>

          <BaseAccordionItem
            v-for="(quickLinkColumn, index) in root.settings?.content.quickLinks"
            :key="index + 1"
            :accordion-index="index + 1"
            :is-open="slotProps.isActiveAccordion(index + 1)"
            @update:is-open="slotProps.setActiveAccordion"
          >
            <template #title>
              <h3>{{ quickLinkColumn.content.headline }}</h3>
            </template>
            <ul
              :aria-label="$t('Aria.Global.Footer.QuickLinkList', { headline: quickLinkColumn.content.headline })"
              class="grid gap-2"
            >
              <li
                v-for="(quickLink, linkIndex) in quickLinkColumn.content.links"
                :key="`link-${linkIndex}`"
              >
                <UmbracoLink
                  :link="quickLink"
                  :has-underline="true"
                  class="text-base"
                />
              </li>
            </ul>
          </BaseAccordionItem>
        </BaseAccordionWrapper>
      </div>
    </div>

    <FooterBottomMenu />
  </footer>
</template>
